import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Button,
  TableBody,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Card,
  CardContent,
  Typography,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import Warning from "@mui/icons-material/Warning";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import {
  fetchAlertsList as fetchAlertsListService,
  fetchAvailableStandards as fetchAvailableStandardsService,
  fetchComplianceAlerts as fetchComplianceAlertsService,
  fetchComplianceStandards as fetchComplianceStandardsService,
} from "services/modules/ecs/alert";
import {
  fetchAvailableDimensions as fetchAvailableDimensionsService,
  fetchSeverities as fetchSeveritiesService,
} from "services/modules/ecs/data";

import * as XLSX from "xlsx";

function AlertsPage() {
  // useLocation hook
  const location = useLocation();

  // React router
  const navigate = useNavigate();

  // Redux dispatch
  const dispatch = useDispatch();
  
  // Redux selectors
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
  const reportDateContainer = useSelector((state) => state.report.latestReportDate);
  const dimensionsContainer = useSelector((state) => state.ecsData.dimensions);
  const complianceStandardsContainer = useSelector((state) => state.ecsAlert.compliancestandards);
  const availableDimensionsContainer = useSelector((state) => state.ecsData.availabledimensions);
  const availablesStandardsContainer = useSelector((state) => state.ecsAlert.availablestandards);
  const severitiesContainer = useSelector((state) => state.ecsData.severities);
  
  // query params
  const searchParams = new URLSearchParams(location.search);
  const alertSeverity = searchParams.get("alert_severity");
  const category = searchParams.get("category");
  const domain = selectedCompanyContainer?.domain;
  const threat_type = searchParams.get("threat_type");
  
  // State variables
  const [severity, setSeverity] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [mainDimension, setMainDimension] = useState(null);
  const [mainDimensions, setMainDimensions] = useState([]);
  const [mainStandards, setMainStandards] = useState([]);
  const [alertsList, setAlertsList] = useState([]);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [subDimension, setSubDimension] = useState("");
  const [loading, setLoading] = useState(false);
  const dimension = dimensionsContainer?.find((dim) => dim.slug === category);
  
  // useEffect hook dispatches the severities and compliance standards service
  useEffect(() => {
    dispatch(fetchSeveritiesService({}));
    dispatch(fetchComplianceStandardsService());

    if (category === "compliance") {
      setSeverity(alertSeverity);
    } else {
      const matchedSeverity = severitiesContainer?.find(
        (s) => s.label === alertSeverity
      );
      setSeverity(matchedSeverity?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Fetches alerts data based on category
  const fetchAlertsData = async (params) => {
    try {
      let response = "";
      setLoading(true);
      if (category !== "compliance")
        response = await fetchAlertsListService({ params });
      else response = await fetchComplianceAlertsService({ params });
      if (response?.status === 200) 
        setAlertsList(response?.data);
    } finally {
      setLoading(false);
    }
  };

  // checks various conditions based on category and appends params for the same
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: "-createdAt",
        domain: domain,
        date: reportDateContainer,
      };
      if (category === "compliance") {
        params.compliant = severity === "Compliant" ? true : false;
        selectedDimension !== null && selectedDimension !== ""  && (params.standard = selectedDimension);
      } else {
        if (severity && severity !== "All") 
          params.severity = severity;
        category === "over-all" && selectedDimension !== null && selectedDimension !== "" && (params.dimension = selectedDimension);
        category !== "over-all" && selectedDimension !== null && selectedDimension !== "" && (params.subDimension = selectedDimension) && (params.dimension = dimension?.id);
        category !== "over-all" && selectedDimension !== null && selectedDimension === "" && (params.dimension = dimension?.id);
      }
      if (threat_type && category === "over-all" && selectedDimension === null)
      {
        const matchedDimension = dimensionsContainer && dimensionsContainer.find((dim) => dim.slug === threat_type);
        params.dimension = matchedDimension?.id;
        setSelectedDimension(matchedDimension?.id);
      }
      if (category !== "over-all" && category !== "compliance" && selectedDimension === null)
      {
        params.dimension = dimension?.id;
        setSubDimension(dimension?.label);
        if (threat_type) {
          const subDimension = dimensionsContainer && dimensionsContainer.find((dim) => dim.slug === threat_type);
          params.subDimension = subDimension?.id;
          setSelectedDimension(subDimension?.id);
        }
      }
      if (threat_type && category === "compliance" && selectedDimension === null)
      {
        const standard = mainStandards && mainStandards.find((st) => st.slug === threat_type);
        setSelectedDimension(standard?.id);
        params.standard = standard?.id;
      }

      if (searchQuery.trim() !== "") {
        params.search = searchQuery.trim();
      }
      fetchAlertsData(params);
      setLoading(false);
    };
    if (severity !== null && dimensionsContainer?.length > 0) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page,rowsPerPage,searchQuery,reportDateContainer,selectedDimension,severity,domain]);
  
  // useEffect hook fetches the available dimensions or standards based on category and severity
  useEffect(() => {
    const params = { domain: domain, date: reportDateContainer };
    if (category === "compliance") {
      severity !== "All" && (params.compliant = severity === "Compliant" ? true : false);
      dispatch(fetchAvailableStandardsService({ params }));
    } else {
      if (category !== "over-all") {
        if (dimension) {
          params.dimension = dimension.id;
        }
      }
    }
    if (severity && severity !== "All") {
      params.severity = severity;
    }
    if (severity !== null && category !== "compliance") {
      dispatch(fetchAvailableDimensionsService({ params }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [severity, domain, reportDateContainer]);
  
  // useEffect hook filters the dimensions and standards and assigns them to a variable based on category
  useEffect(() => {
    if (category === "over-all") {
      const filteredMainDimensions = dimensionsContainer?.filter(
        (dim) => !!dim.subDimensions
      );
      if (filteredMainDimensions !== null)
        setMainDimensions(filteredMainDimensions);
    } else if (category === "compliance") {
      const mainStandards = complianceStandardsContainer?.filter((standard) => standard.mapping && standard.mapping.length > 0).map((standard) => ({
          label: standard.label,
          slug: standard.slug,
          id: standard.id,
        }));
      if (mainStandards !== null) 
        setMainStandards(mainStandards);
    } else {
      if (dimension && dimension.subDimensions)
        setMainDimensions(dimension.subDimensions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionsContainer,complianceStandardsContainer]);
  
  // Handles Page Change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  // Handles navigation to single alert page by clicking on finding
  const handleTitleClick = (id) => {
    navigate(`/crs/threat/${id}?domain=${domain}`);
  };
  
  // Handles change in rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filters articles and concatenates them by a comma for compliance category
  const getArticles = (dimension) => {
    const articles = [];

    complianceStandardsContainer.forEach((standard) => {
      standard.mapping.forEach((map) => {
        if (map.dimension.id === dimension) {
          articles.push(...map.articles);
        }
      });
    });

    return articles.join(", ");
  };
  
  // Returns dimension name 
  const getDimensionName = (dimension) => {
    const mainDimension = dimensionsContainer
      ?.filter((dim) => !!dim.subDimensions)
      .find((dim) => dim.subDimensions.find((sub) => sub.id === dimension));

    if (mainDimension) {
      return mainDimension.label;
    }
  };

  // Handles change in search query
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };
  
  // Handles main dimension click event
  const handleMainDimensionClick = async (dimension) => {
    setPage(0);
    if (selectedDimension != null) 
      setSelectedDimension("");
    if (selectedDimension !== dimension.id) 
      setSelectedDimension(dimension.id);
  };
  
  // Handles excel version of alert information when user clicks on downlload after selecting a dimension or subdimension
  const exportToExcel = async () => {
    if (category === "compliance" || !mainDimension) 
      return;
    const params = { sortBy: "-createdAt", domain: domain, date: reportDateContainer };
    category === "over-all" ? (params.dimension = mainDimension) : (params.subDimension = mainDimension);
    let response = await fetchAlertsListService({ params });
    const dataToExport = response?.data?.results.map((alert) => ({
      finding: alert.title,
      dimension: alert.dimension.label,
      impact: alert.impact,
      severity: getSeverityLabel(alert.score),
      score: alert.score,
      recommendations: alert.remediation,
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    let filename = "";
    if (category === "over-all")
      filename = `${mainDimensions.find((d) => d.id === mainDimension)?.label || "default"}.xlsx`;
    else
      filename = `${subDimension}-${mainDimensions.find((d) => d.id === mainDimension)?.label || "default"}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  // Returns alert severity
  const getSeverityLabel = useCallback((score) => {
      const severityObj = severitiesContainer.find((s) => {
        let allowed = true;
        if (s.range.gte !== undefined && allowed) {
          allowed = score >= s.range.gte;
        }
        if (s.range.lte !== undefined && allowed) {
          allowed = score <= s.range.lte;
        }
        if (s.range.gt !== undefined && allowed) {
          allowed = score > s.range.gt;
        }
        if (s.range.lt !== undefined && allowed) {
          allowed = score < s.range.lt;
        }
        return allowed;
      });

      return severityObj ? severityObj.label : "";
    },[severitiesContainer]);

  return (
    <div className="main-cont">
      <div className="main-cont">
        <div className="row">
          <Card variant="outlined" className="menu-list">
            <CardContent>
              <div className="dimension-card">
                {category === "compliance"
                  ? mainStandards &&
                    mainStandards.map((standard) => (
                      <Typography
                        key={standard.id}
                        variant="subtitle2"
                        component="div"
                        onClick={() =>
                          availablesStandardsContainer.some(
                            (ad) => ad.id === standard.id
                          ) && handleMainDimensionClick(standard)
                        }
                        className={`
        ${
          availablesStandardsContainer.some((ad) => ad.id === standard.id)
            ? "available-dimensions"
            : "not-available-dimensions"
        }
        ${selectedDimension === standard.id ? "selected-dimension" : ""}
      `}
                      >
                        {standard.label}
                      </Typography>
                    ))
                  : mainDimensions &&
                    mainDimensions.map((dimension) => (
                      <Typography
                        key={dimension.id}
                        variant="subtitle2"
                        component="div"
                        onClick={() =>
                          availableDimensionsContainer.some(
                            (ad) => ad.id === dimension.id
                          ) && handleMainDimensionClick(dimension)
                        }
                        className={`
        ${
          availableDimensionsContainer.some((ad) => ad.id === dimension.id)
            ? "available-dimensions"
            : "not-available-dimensions"
        }
        ${selectedDimension === dimension.id ? "selected-dimension" : ""}
      `}
                      >
                        {dimension.label}
                      </Typography>
                    ))}
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="row">
          <div className="col-lg-2 col-md-4 col-sm-12 col-xs-12 search-ecs">
            <TextField
              variant="outlined"
              placeholder="Search"
              value={searchQuery}
              fullWidth
              size="small"
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="col-lg-2 col-md-4 col-sm-12 col-xs-12 search-ecs">
            <FormControl variant="outlined" fullWidth size="small">
              <Select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
              >
                {category === "compliance"
                  ? [
                      <MenuItem key="All" value="All">
                        All
                      </MenuItem>,
                      <MenuItem key="Compliant" value="Compliant">
                        Compliant
                      </MenuItem>,
                      <MenuItem key="NonCompliant" value="Non-Compliant">
                        Non-Compliant
                      </MenuItem>,
                    ]
                  : [
                      <MenuItem value="All">All</MenuItem>,
                      ...severitiesContainer.map((severity) => (
                        <MenuItem key={severity.id} value={severity.id}>
                          {severity.label}
                        </MenuItem>
                      )),
                    ]}
              </Select>
            </FormControl>
          </div>

          <div className="col-lg-3"></div>

          <div className="col-lg-3 col-md-3 col-sm-12 col-xs-12 search-ecs">
            <FormControl variant="outlined" autoWidth size="small" fullWidth>
              <InputLabel>Choose One</InputLabel>
              <Select
                value={mainDimension}
                onChange={(event) => setMainDimension(event.target.value)}
                label="Choose One"
              >
                {mainDimensions &&
                  mainDimensions.map((dimension) => (
                    <MenuItem key={dimension.id} value={dimension.id}>
                      {dimension.label}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </div>
          <div className="col-lg-2 col-md-2 col-sm-12 col-xs-12 search-ecs">
            <Button variant="contained" onClick={exportToExcel}>
              Download
            </Button>
          </div>
        </div>
        <div>
          {alertsList && alertsList.results ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    {category === "compliance" && (
                      <TableCell align="center" className="table-headers">
                        Articles
                      </TableCell>
                    )}
                    <TableCell
                      align="center"
                      className="table-headers"
                      style={{ width: "5%" }}
                    >
                      Finding
                    </TableCell>
                    <TableCell align="center" className="table-headers">
                      Severity
                    </TableCell>
                    <TableCell align="center" className="table-headers">
                      Sub dimension
                    </TableCell>
                    <TableCell align="center" className="table-headers">
                      Impact
                    </TableCell>
                    <TableCell align="center" className="table-headers">
                      Score
                    </TableCell>
                    <TableCell align="center" className="table-headers">
                      Remediation/Recommendation
                    </TableCell>
                    {category === "compliance" && (
                      <TableCell align="center" className="table-headers">
                        Compliance
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : alertsList.results.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Warning color="black" fontSize="small" />
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    alertsList.results.map((alert, index) => (
                      <TableRow key={index}>
                        {category === "compliance" && (
                          <TableCell
                            align="center"
                            className="table-body-articles"
                          >
                            {getArticles(alert.dimension.id)}
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <div>
                            <p
                              className="alert-text margin-class"
                              onClick={() => handleTitleClick(alert.id)}
                            >
                              {alert.title}
                            </p>{" "}
                            <p className="margin-class text-grey-7 ">
                              {alert.dimension.label &&
                                getDimensionName(alert.dimension.id)}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          <div className="rating-value">
                            <span
                              className={`${
                                getSeverityLabel(alert.score)
                                  ? getSeverityLabel(alert.score).toLowerCase()
                                  : ""
                              }`}
                            >
                              {getSeverityLabel(alert.score).charAt(0)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          {" "}
                          {alert.dimension.label}
                        </TableCell>
                        <TableCell align="center">
                          {alert.impact.substring(0, 20)}...
                        </TableCell>
                        <TableCell align="center">{alert.score}</TableCell>
                        <TableCell align="center">
                          {alert.remediation.substring(0, 20)}...
                        </TableCell>
                        {category === "compliance" && (
                          <TableCell align="center">
                            {alert.score === 0 ? (
                              <CheckIcon className="success" />
                            ) : (
                              <CloseIcon color="error" />
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <p>{loading ? "Loading..." : "No Data Available"}</p>
          )}
          <TablePagination
            rowsPerPageOptions={[5, 7, 10, 15, 25]}
            component="div"
            count={alertsList.results ? alertsList.totalResults : 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </div>
    </div>
  );
}

export default AlertsPage;
